/**
 * Simplified Permission System Types and Constants
 *
 * Based on backend simplified permission implementation with only 4 permission types total.
 * Used for frontend UI/UX control - backend enforces actual security.
 *
 * SIMPLIFIED PERMISSION MODEL:
 * - System permissions: admin (full control), viewer (read-only everything)
 * - Resource permissions: read (view resource), write (full control including delete)
 * - Resource types: system, folder, feature, organization, trial
 * - Permission levels: system, resource
 * - UserPermission model with proper constraints
 */

// Permission resource types
export const RESOURCE_TYPES = {
  SYSTEM: 'system',
  FOLDER: 'folder',
  FEATURE: 'feature',
  ORGANIZATION: 'organization',
  TRIAL: 'trial',
} as const;

export type ResourceType = (typeof RESOURCE_TYPES)[keyof typeof RESOURCE_TYPES];

// System permissions - Only 2 types!
export const SYSTEM_PERMISSIONS = {
  ADMIN: 'admin', // Full platform control
  VIEWER: 'viewer', // Read-only access to everything
} as const;

// Resource permissions - Only 2 types!
export const RESOURCE_PERMISSIONS = {
  READ: 'read', // View resource and children
  WRITE: 'write', // Full control (includes delete)
} as const;

export type SystemPermission = (typeof SYSTEM_PERMISSIONS)[keyof typeof SYSTEM_PERMISSIONS];
export type ResourcePermission = (typeof RESOURCE_PERMISSIONS)[keyof typeof RESOURCE_PERMISSIONS];
export type Permission = SystemPermission | ResourcePermission;

// Permission levels
export enum PermissionLevel {
  SYSTEM = 'system', // Platform-wide permissions
  RESOURCE = 'resource', // Specific resource permissions
}

// Permission sources (where permission comes from) - these are response metadata, not backend constants
export const PERMISSION_SOURCES = {
  SYSTEM: 'system',
  DIRECT: 'direct',
  INHERITED: 'inherited',
} as const;

export type PermissionSource = (typeof PERMISSION_SOURCES)[keyof typeof PERMISSION_SOURCES];

// Single permission check
export interface PermissionCheck {
  resource_type: ResourceType;
  resource_id?: string | null;
  permission: Permission;
}

// Permission check result
export interface PermissionCheckResult {
  resource_type: string;
  resource_id?: string | null;
  permission: string;
  has_permission: boolean;
  source?: string | null; // String from API, matches PermissionCheckResultItem
}

// Bulk permission check request/response
export interface PermissionCheckRequest {
  checks: PermissionCheck[];
}

export interface PermissionCheckResponse {
  results: Record<string, PermissionCheckResult>;
  checked_at: string;
  cache_ttl_seconds?: number; // Optional to match backend API response
}

// Helper functions for creating permission checks
export const createSystemPermissionCheck = (permission: SystemPermission): PermissionCheck => ({
  resource_type: RESOURCE_TYPES.SYSTEM,
  resource_id: null,
  permission,
});

export const createResourcePermissionCheck = (
  resource_type: ResourceType,
  permission: ResourcePermission,
  resource_id?: string
): PermissionCheck => ({
  resource_type,
  resource_id: resource_id || null,
  permission,
});

// Permission check result helpers
export const generatePermissionKey = (check: PermissionCheck): string => {
  const resourceId = check.resource_id || 'None';
  return `${check.resource_type}:${resourceId}:${check.permission}`;
};

export const hasPermission = (
  results: Record<string, PermissionCheckResult>,
  check: PermissionCheck
): boolean => {
  const key = generatePermissionKey(check);
  return results[key]?.has_permission ?? false;
};

// DMS-specific feature IDs and checks — re-exported for backward compatibility.
// Canonical source: @/features/dms/constants/permissions
export { FEATURE_CHECKS, FEATURE_IDS } from '@/features/dms/constants/permissions';

// Common permission check shortcuts
export const COMMON_CHECKS = {
  // System permissions - Only 2 types!
  ADMIN: createSystemPermissionCheck(SYSTEM_PERMISSIONS.ADMIN),
  VIEWER: createSystemPermissionCheck(SYSTEM_PERMISSIONS.VIEWER),

  // Resource-scoped helpers — resolve to system-level admin checks.
  // The `_resourceId` parameter is accepted for forward-compatibility but unused.
  trialWrite: (_resourceId?: string): PermissionCheck =>
    createSystemPermissionCheck(SYSTEM_PERMISSIONS.ADMIN),
} as const;
